import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { detectRegion } from '../../config/region';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isProcessing) return;

    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const loginMethod = params.get('loginMethod');
    const email = params.get('email');
    const name = params.get('name');
    const role = params.get('role');
    const region = params.get('region') || detectRegion();
    const userId = params.get('userId');
    const hlsourcetoken = params.get('hlsourcetoken');
    const utmcampaign = params.get('utm_campaign');
    const utmsource = params.get('utm_source');
    const completeProfile = params.get('complete_profile');
    const inviteToken = params.get('invite_token');

    if (token) {
      setIsProcessing(true);

      // Store authentication token (both keys for compatibility)
      localStorage.setItem('venueapp_token', token);
      localStorage.setItem('venue_app_token', token);
      localStorage.setItem('token', token);

      // Store additional user info
      if (loginMethod) localStorage.setItem('user_loginmethod', loginMethod);
      if (email) localStorage.setItem('user_email', email);
      if (name) localStorage.setItem('user_name', name);
      if (role) localStorage.setItem('user_role', role);
      if (userId) localStorage.setItem('user_id', userId);
      const profileImage = params.get('profile_image');
      if (profileImage) localStorage.setItem('user_profile_image', profileImage);
      if (region) localStorage.setItem('user_region', region);
      if (hlsourcetoken) localStorage.setItem('user_qrtoken', hlsourcetoken);
      if (utmcampaign) localStorage.setItem('utm_campaign', utmcampaign);
      if (utmsource) localStorage.setItem('utm_source', utmsource);

      // Set locale from backend (detected from LINE display name)
      const locale = params.get('locale');
      if (locale && locale !== i18n.language) {
        i18n.changeLanguage(locale);
        localStorage.setItem('hl_locale', locale);
      }

      console.log('OAuth login successful', { region, email, loginMethod, locale });

      // Resolve invite token — prefer URL param (from state), fall back to localStorage
      // (localStorage is used when state param can't be passed through passport due to session:false)
      const pendingInvite = inviteToken
        || localStorage.getItem('pending_invite_token')
        || sessionStorage.getItem('pending_invite_token');

      // Clean up stored tokens
      localStorage.removeItem('pending_invite_token');
      sessionStorage.removeItem('pending_invite_token');

      // Determine redirect destination
      const isVenueStaff = params.get('is_venue_staff') === '1';
      const venueSlug = params.get('venue_slug');

      // v69.193 — an explicit "log in to do X" intent (post_login_redirect, e.g. a
      // birthday invite RSVP at /b/<id>) MUST win over the venue-owner auto-redirect.
      // Without this, an owner/admin who taps RSVP is dumped on the venue dashboard
      // instead of returning to their invite — losing the RSVP intent (Patrick 2026-06-21).
      const postLoginRedirect = sessionStorage.getItem('post_login_redirect');

      if (completeProfile === '1') {
        // LINE first-time login — profile completion required
        // Re-stash invite token so complete-profile can forward it
        if (pendingInvite) sessionStorage.setItem('pending_invite_token', pendingInvite);
        // Remember venue staff status for after profile completion
        if (isVenueStaff) sessionStorage.setItem('post_auth_venue_staff', '1');
        if (venueSlug) sessionStorage.setItem('post_auth_venue_slug', venueSlug);
        window.location.href = '/complete-profile';
      } else if (pendingInvite) {
        // Invite flow — redirect to join page to accept invite
        window.location.href = '/join?token=' + encodeURIComponent(pendingInvite);
      } else if (postLoginRedirect && postLoginRedirect.startsWith('/')) {
        // Explicit return-to-page intent wins (RSVP, claim, bookmark, etc.)
        sessionStorage.removeItem('post_login_redirect');
        window.location.href = postLoginRedirect;
      } else if (isVenueStaff) {
        // Venue owner/staff — redirect to venue dashboard
        const dashPath = venueSlug ? `/venue-dashboard/${venueSlug}` : '/venue-dashboard';
        window.location.href = dashPath;
      } else {
        // Default redirect — consumer map
        window.location.href = '/';
      }
    } else {
      // No token found, redirect to login with error
      const error = params.get('error') || 'authentication_failed';
      const message = params.get('message') || 'Authentication failed';
      console.error('OAuth error:', error, message);
      navigate(`/login?error=${error}&message=${encodeURIComponent(message)}`, { replace: true });
    }
  }, [location.search, navigate, isProcessing]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        fontWeight: 'bold',
        background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)',
        color: 'white',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          padding: '2rem',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '1rem',
          backdropFilter: 'blur(10px)',
        }}
      >
        <div
          style={{
            width: '3rem',
            height: '3rem',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem',
          }}
        />
        <div>{t('auth.loggingIn')}</div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default AuthCallback;
